import React, { useMemo } from "react";
import useVM from "@src/hooks/useVM";
import { makeAutoObservable } from "mobx";
import { RootStore, useStores } from "@stores";
import * as demosService from "@services/demosService";
import { Profile } from "@services/demosService";

const ctx = React.createContext<ProfileScreenVM | null>(null);

export const ProfileScreenVMProvider: React.FC = ({ children }) => {
  const rootStore = useStores();
  const store = useMemo(() => new ProfileScreenVM(rootStore), [rootStore]);
  return <ctx.Provider value={store}>{children}</ctx.Provider>;
};

export const useProfileScreenVM = () => useVM(ctx);

const defaultProfile: Profile = {
  email: "",
  full_name: "",
  organization_name: "",
  phone_number: "",
};

class ProfileScreenVM {
  loading: boolean = true;
  private setLoading = (v: boolean) => (this.loading = v);

  profileData: Profile;
  private setProfileData = (v: Profile) => (this.profileData = v);

  formData: Profile;
  setFormData = (v: Profile) => (this.formData = v);

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);

    this.formData = defaultProfile;
    this.profileData = defaultProfile;

    this.sync()
      .then(this.resetFormData)
      .then(() => this.setLoading(false));
  }

  resetFormData = () => {
    if (this.profileData) {
      this.setFormData(this.profileData);
    }
  }

  updateProfileData = async () => {
    if (this.formData !== null) {
      await demosService.updateProfile(
        this.formData.full_name,
        this.formData.phone_number,
        this.formData.organization_name
      );
      // Refresh the account so the new name propagates to the rest of the UI
      // (sidebar, Chatwoot) and re-sync the form's baseline.
      await this.rootStore.accountStore.auth();
      await this.sync();
      this.resetFormData();
    }
  };

  sync = async () => {
    const account = this.rootStore.accountStore.account;
    if (account) {
      this.setProfileData({
        email: account.email,
        full_name: account.name ?? "",
        phone_number: account.phoneNumber ?? "",
        organization_name: account.organizationName ?? "",
      });
    }
  }
}
